/**
 * Generic lifecycle-state derivation for internal tasks.
 *
 * Every internal task's state is derived from the standard lifecycle fields
 * (`completedDate`, `approvalDate`) plus one config flag (`hasPendingPhase`), rather
 * than stored on the record or branched on by task name. This keeps the UI and any
 * consuming lambda free of per-task conditionals — a new multi-phase task only needs
 * to set `hasPendingPhase` and provide a state-aware `description()`.
 *
 * Pure functions, zero runtime dependencies (safe for frontend and backend).
 */

/**
 * The `approvedBy` value stamped when the AI review auto-approves a clean inspection
 * (see ai-be inspection-reporter-cron). Shared so the writer and the UI's auto-approved
 * detection can't drift. Manual staff approvals stamp a Cognito username instead.
 */
export const AI_INSPECTION_REVIEWER = 'AI Inspection Review';

/**
 * - `pending`     — has a pre-review phase and hasn't completed it yet (no approval panel).
 * - `needsReview` — completed (or reviewable-on-create) but not yet approved (approval panel shown).
 * - `approved`    — approved (auto or manual).
 */
export type InternalTaskState = 'pending' | 'needsReview' | 'approved';

interface TaskLifecycleFields {
  completedDate?: string;
  approvalDate?: string;
}

export function getInternalTaskState(
  { completedDate, approvalDate }: TaskLifecycleFields,
  config?: { hasPendingPhase?: boolean }
): InternalTaskState {
  if (approvalDate) {
    return 'approved';
  }
  if (config?.hasPendingPhase && !completedDate) {
    return 'pending';
  }
  return 'needsReview';
}

/**
 * Auto-approved vs manually-approved is told by `approvedBy` — NOT by timestamp equality.
 * The internal-task approval path stamps `completedDate` and `approvalDate` at the same
 * instant for BOTH auto- and manual approvals, so equal timestamps don't distinguish them;
 * only the approver (the AI reviewer vs a Cognito username) does.
 */
export function isAutoApprovedInternalTask({ approvedBy }: { approvedBy?: string }): boolean {
  return approvedBy === AI_INSPECTION_REVIEWER;
}
